import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { useAtomValue } from 'jotai'
import { SimpleGrid, TextInput, Loader, Text, Center, Stack, Switch, Group } from '@mantine/core'
import { IconSearch, IconWorld } from '@tabler/icons-react'
import { isAuthenticatedAtom } from '../../atoms/authAtoms'
import { showPublicStoriesAtom } from '../../atoms/uiAtoms'
import { storyService } from '../../services/storyService'
import { StoryCard } from './StoryCard'

export function StoryCatalog() {
  const [search, setSearch] = useState('')
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const [showPublic, setShowPublic] = useAtom(showPublicStoriesAtom)

  const { data: stories, isLoading } = useQuery({
    queryKey: ['stories'],
    queryFn: () => storyService.getStories(),
  })

  const playable = (stories || []).filter((s) => s.threads_count > 0)
  const hasGroupStories = playable.some((s) => s.in_user_group)

  // null = no explicit preference yet — default based on group membership
  const effectiveShowPublic = showPublic === null ? !hasGroupStories : showPublic

  const filtered = playable.filter((s) => {
    if (search && !s.name?.toLowerCase().includes(search.toLowerCase())) return false
    if (!isAuthenticated) return s.is_public
    // Group stories always show; public stories controlled by toggle
    if (s.in_user_group) return true
    if (effectiveShowPublic) return true
    return false
  })

  return (
    <Stack gap="md">
      {isAuthenticated && (
        <Group justify="flex-end">
          <Switch
            label="Public stories"
            checked={effectiveShowPublic}
            onChange={(e) => setShowPublic(e.currentTarget.checked)}
            thumbIcon={<IconWorld size={12} stroke={2.5} color="rgba(0, 255, 255, 0.8)" />}
            styles={{
              label: { color: '#8899aa', fontSize: '0.85rem', paddingLeft: 8 },
              track: { cursor: 'pointer' },
            }}
          />
        </Group>
      )}
      <TextInput
        placeholder="Search stories..."
        leftSection={<IconSearch size={16} />}
        rightSection={isLoading ? <Loader size="xs" /> : null}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        styles={{
          input: {
            background: 'var(--bw-panel-bg)',
            border: '1px solid var(--bw-panel-border)',
            color: 'var(--bw-body-color)',
            '&:focus': {
              borderColor: 'var(--bw-accent)',
            },
          },
        }}
      />

      {filtered.length === 0 && !isLoading ? (
        <Center py="xl">
          <Text c="dimmed">
            {search ? 'No stories match your search.' : 'No stories available yet.'}
          </Text>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {filtered.map((story) => (
            <StoryCard key={story.id} story={story} />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  )
}
